const DECIMAL = /^-?\d+(\.\d+)?$/;

function assertScale(scale: number): void {
  if (!Number.isInteger(scale) || scale < 0) {
    throw new RangeError(`scale must be a non-negative integer, got ${scale}`);
  }
}

/**
 * A decimal string as integer minor units: `toMinor("1250.5", 3)` → `1250500n`.
 * Throws a `RangeError` for anything but a plain decimal (`"1e3"`, `"1,000"`,
 * `".5"`, `""`) and for more fraction digits than `scale` — it never rounds.
 */
export function toMinor(decimal: string, scale: number): bigint {
  assertScale(scale);
  const value = decimal.trim();
  if (!DECIMAL.test(value)) throw new RangeError(`"${decimal}" is not a plain decimal`);
  const negative = value.startsWith("-");
  const [whole = "", fraction = ""] = (negative ? value.slice(1) : value).split(".");
  if (fraction.length > scale) {
    throw new RangeError(`"${decimal}" has more than ${scale} fraction digits`);
  }
  const minor = BigInt(whole + fraction.padEnd(scale, "0"));
  return negative ? -minor : minor;
}

/**
 * Minor units back to a decimal string with exactly `scale` fraction digits:
 * `fromMinor(-1250500n, 3)` → `"-1250.500"`, `fromMinor(5n, 3)` → `"0.005"`.
 */
export function fromMinor(minor: bigint, scale: number): string {
  assertScale(scale);
  const digits = (minor < 0n ? -minor : minor).toString().padStart(scale + 1, "0");
  const whole = digits.slice(0, digits.length - scale);
  const fraction = digits.slice(digits.length - scale);
  return `${minor < 0n ? "-" : ""}${whole}${scale > 0 ? `.${fraction}` : ""}`;
}

function sumOf(amounts: readonly string[], scale: number): bigint {
  return amounts.reduce((total, amount) => total + toMinor(amount, scale), 0n);
}

/** Do the debits and the credits sum to exactly the same amount? */
export function isBalanced(
  debits: readonly string[],
  credits: readonly string[],
  scale: number,
): boolean {
  assertScale(scale);
  return sumOf(debits, scale) === sumOf(credits, scale);
}

/** `a + b` at `scale`: `addMinor("1250.5", "-0.25", 3)` → `"1250.250"`. */
export function addMinor(a: string, b: string, scale: number): string {
  return fromMinor(toMinor(a, scale) + toMinor(b, scale), scale);
}

/** `a - b` at `scale`: `subMinor("320", "400", 3)` → `"-80.000"`. */
export function subMinor(a: string, b: string, scale: number): string {
  return fromMinor(toMinor(a, scale) - toMinor(b, scale), scale);
}

/** The sum of `amounts` at `scale`; an empty list sums to zero (`"0.000"` at scale 3). */
export function sumMinor(amounts: readonly string[], scale: number): string {
  assertScale(scale);
  return fromMinor(sumOf(amounts, scale), scale);
}

/** Compares two amounts at `scale`: `-1` when `a < b`, `0` when equal, `1` when `a > b`. */
export function cmpMinor(a: string, b: string, scale: number): -1 | 0 | 1 {
  const difference = toMinor(a, scale) - toMinor(b, scale);
  return difference < 0n ? -1 : difference > 0n ? 1 : 0;
}

/** Matches a decimal string worth nothing, at any scale or sign: `"0"`, `"0.000"`, `"-0.00"`. */
export const ZERO = /^-?0(\.0*)?$/;
